// existingFields.nip/customerName come from show_latest_delivery_order
// (ticket 01) when it succeeds — falls back to "" (whatever's on screen)
// otherwise, which naturally routes an untouched field through the
// "auto-fill" status below.

import { OrderMetaData } from "@/components/maps/AnalyzeOrderVerifyStops"
import type { FormFields } from "@/components/maps/orderDetails/OrderDetailsDialog"

export type CompareFieldKey =
  | "order_number"
  | "driver_name"
  | "car_plates"
  | "trailer_plates"
  | "ref_number"
  | "price_netto_eur"
  | "nip"
  | "customer_name"
  | "coordinator_name"
  | "empty_kilometers"
  | "loaded_kilometers"

// Translation keys, not text: the list calls t() when it renders.
const FIELD_LABELS = {
  order_number: "orders.orderDetails.fields.orderNumber",
  driver_name: "orders.orderDetails.fields.driverName",
  car_plates: "orders.orderDetails.fields.carPlates",
  trailer_plates: "orders.orderDetails.fields.trailerPlates",
  ref_number: "orders.orderDetails.fields.referenceNumber",
  price_netto_eur: "orders.orderDetails.fields.priceNet",
  nip: "orders.orderDetails.fields.nip",
  customer_name: "orders.orderDetails.fields.customerName",
  coordinator_name: "orders.orderDetails.fields.coordinatorName",
  empty_kilometers: "orders.orderDetails.fields.emptyKilometers",
  loaded_kilometers: "orders.orderDetails.fields.loadedKilometers",
} as const satisfies Record<CompareFieldKey, string>

export type CompareField = {
  key: CompareFieldKey
  label: (typeof FIELD_LABELS)[CompareFieldKey]
  oldValue: string
  newValue: string
  editable: boolean
}

// Every field is editable — gps's update_delivery_order (mbugno/delivery-order-api)
// now persists driver_name/car_plates/trailer_plates too, not just the original
// 4-field merge scope.
const EDITABLE_FIELDS = new Set<CompareFieldKey>([
  "order_number",
  "driver_name",
  "car_plates",
  "trailer_plates",
  "ref_number",
  "price_netto_eur",
  "nip",
  "customer_name",
  "coordinator_name",
  "empty_kilometers",
  "loaded_kilometers",
])

const formatPrice = (price?: string, currency?: string) =>
  price ? `${price} ${currency || "EUR"}` : ""

export const buildCompareFields = (
  existingFields: FormFields,
  extracted: OrderMetaData,
): CompareField[] => [
  {
    key: "order_number",
    label: FIELD_LABELS.order_number,
    oldValue: existingFields.orderNumber,
    newValue: extracted.order_number || "",
    editable: EDITABLE_FIELDS.has("order_number"),
  },
  {
    key: "customer_name",
    label: FIELD_LABELS.customer_name,
    oldValue: existingFields.customerName,
    newValue: extracted.customer_name || "",
    editable: EDITABLE_FIELDS.has("customer_name"),
  },
  {
    key: "nip",
    label: FIELD_LABELS.nip,
    oldValue: existingFields.nip,
    newValue: extracted.nip || "",
    editable: EDITABLE_FIELDS.has("nip"),
  },
  {
    key: "coordinator_name",
    label: FIELD_LABELS.coordinator_name,
    oldValue: existingFields.coordinatorName,
    newValue: extracted.coordinator_name || "",
    editable: EDITABLE_FIELDS.has("coordinator_name"),
  },
  {
    key: "ref_number",
    label: FIELD_LABELS.ref_number,
    oldValue: existingFields.referenceNumber,
    newValue: extracted.ref_number || "",
    editable: EDITABLE_FIELDS.has("ref_number"),
  },
  {
    key: "price_netto_eur",
    label: FIELD_LABELS.price_netto_eur,
    oldValue: formatPrice(existingFields.price, existingFields.priceCurrency),
    newValue:
      extracted.price_netto_eur !== undefined
        ? `${extracted.price_netto_eur.toFixed(2)} EUR`
        : "",
    editable: EDITABLE_FIELDS.has("price_netto_eur"),
  },
  {
    key: "empty_kilometers",
    label: FIELD_LABELS.empty_kilometers,
    oldValue: existingFields.emptyKilometers,
    newValue: extracted.empty_kilometers || "",
    editable: EDITABLE_FIELDS.has("empty_kilometers"),
  },
  {
    key: "loaded_kilometers",
    label: FIELD_LABELS.loaded_kilometers,
    oldValue: existingFields.loadedKilometers,
    newValue: extracted.loaded_kilometers || "",
    editable: EDITABLE_FIELDS.has("loaded_kilometers"),
  },
  {
    key: "driver_name",
    label: FIELD_LABELS.driver_name,
    oldValue: existingFields.driverName,
    newValue: extracted.driver_name || "",
    editable: EDITABLE_FIELDS.has("driver_name"),
  },
  {
    key: "car_plates",
    label: FIELD_LABELS.car_plates,
    oldValue: existingFields.carPlates,
    newValue: extracted.car_plates || "",
    editable: EDITABLE_FIELDS.has("car_plates"),
  },
  {
    key: "trailer_plates",
    label: FIELD_LABELS.trailer_plates,
    oldValue: existingFields.trailerPlates,
    newValue: extracted.trailer_plates || "",
    editable: EDITABLE_FIELDS.has("trailer_plates"),
  },
]

const normalize = (v: unknown): string => (v == null ? "" : String(v).trim())

export const hasConflict = (field: CompareField) =>
  normalize(field.oldValue) !== normalize(field.newValue)

// Three different kinds of "different" need three different UIs:
// - nothing there before, new document fills it in — just accept it
// - there was a value, new document didn't find one — nothing to decide, keep it
// - both sides have a (different) value — an actual conflict to resolve
export type FieldStatus = "unchanged" | "auto-fill" | "kept" | "conflict"

export const fieldStatus = (field: CompareField): FieldStatus => {
  if (!hasConflict(field)) return "unchanged"
  const oldHas = Boolean(normalize(field.oldValue))
  const newHas = Boolean(normalize(field.newValue))
  if (!oldHas && newHas) return "auto-fill"
  if (oldHas && !newHas) return "kept"
  return "conflict"
}

export type FieldSource = "old" | "new" | "manual"

export type FieldResolution = {
  source: FieldSource
  value: string
}

export type ResolutionState = Record<CompareFieldKey, FieldResolution>

export const buildInitialResolution = (
  fields: CompareField[],
): ResolutionState =>
  Object.fromEntries(
    fields.map((field) => [
      field.key,
      // Don't default to blanking a real value just because the new
      // document didn't happen to carry that field.
      hasConflict(field) && normalize(field.newValue)
        ? { source: "new" as const, value: field.newValue }
        : { source: "old" as const, value: field.oldValue },
    ]),
  ) as ResolutionState
